/**
 * Webview utilities — audio unlock/playback + WebSocket URL builder.
 */
import { buildApiUrl } from '../config/api';
import { getStoredToken } from './api';

// ─── Route / fetch auth helpers (same module as webview audio + WS) ───────────

const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

export function isWebViewLocalhostBypass() {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return false;
  }
  return LOCALHOST_HOSTNAMES.has(window.location.hostname);
}

const isTruthyEnvFlag = (raw) => {
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
};

export function isWebViewAuthBypass() {
  if (isWebViewLocalhostBypass()) return true;
  return isTruthyEnvFlag(process.env.REACT_APP_WEBVIEW_AUTH_BYPASS);
}

export function resolveCompanyLogoSrc(
  logo,
  apiBase = '',
  companyId = null,
  fallback = '/assets/icons/kivo-fav-icon.png',
) {
  if (!logo || !String(logo).trim()) return fallback;
  const value = String(logo).trim();
  if (value.startsWith('data:')) return value;
  if (value.startsWith('/assets/') || value === '/Rexnord-logo.png') return value;

  let pathname = value;
  try {
    if (/^https?:\/\//i.test(value)) {
      pathname = new URL(value).pathname;
    }
  } catch {
    pathname = value;
  }
  if (!pathname.includes('company_logo') && !pathname.includes('/settings/company/logo')) {
    return value;
  }

  const base = String(apiBase || '').replace(/\/+$/, '');
  const params = new URLSearchParams();
  if (companyId) params.set('company_id', String(companyId));
  const query = params.toString();
  return `${base}/api/v1/settings/company/logo${query ? `?${query}` : ''}`;
}

// Logo URL -> Promise<blob URL> holding the freshest bytes for this document.
// The logo API is served with a long max-age, so images added after page load
// would otherwise keep showing a stale copy (including after a hard reload).
const companyLogoBlobUrls = new Map();

const isCompanyLogoApiUrl = (src) =>
  typeof src === 'string' && src.includes('/settings/company/logo');

const isPageReload = () => {
  try {
    const [navigation] = performance.getEntriesByType('navigation');
    return navigation?.type === 'reload';
  } catch {
    return false;
  }
};

/** Download the logo bypassing the browser cache, store it, and return a blob URL for display. */
export function refreshCompanyLogoCache(src, headers = {}) {
  if (!isCompanyLogoApiUrl(src)) return Promise.resolve(src);
  const pending = fetch(src, { cache: 'reload', headers })
    .then(async (response) => {
      if (!response.ok) throw new Error(`Logo request failed with status ${response.status}`);
      return URL.createObjectURL(await response.blob());
    })
    .catch((err) => {
      console.error('Failed to refresh company logo:', err);
      companyLogoBlobUrls.delete(src);
      return src;
    });
  companyLogoBlobUrls.set(src, pending);
  return pending;
}

/** Logo src to render: refreshed once per page reload, otherwise served from the browser cache. */
export function getCompanyLogoSrc(src, headers = {}) {
  if (!isCompanyLogoApiUrl(src)) return Promise.resolve(src);
  if (companyLogoBlobUrls.has(src)) return companyLogoBlobUrls.get(src);
  if (isPageReload()) return refreshCompanyLogoCache(src, headers);
  return Promise.resolve(src);
}

/** Optional Bearer for webview HTTP calls when a session token exists. */
export function buildWebviewFetchHeaders(baseHeaders = {}) {
  const token = getStoredToken();
  if (!token) return baseHeaders;
  return {
    ...baseHeaders,
    Authorization: `Bearer ${token}`,
  };
}

/** Parse `/webview?camera=uuid1&camera=uuid2` (also accepts camera_id and comma-separated). */
export function parseCameraIdsFromSearch(search = '') {
  const rawSearch = String(search || '');
  const query = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
  const params = new URLSearchParams(query);
  const seen = new Set();
  const ordered = [];
  const addValue = (raw) => {
    String(raw || '')
      .split(',')
      .forEach((part) => {
        const id = part.trim().toLowerCase();
        if (id && !seen.has(id)) {
          seen.add(id);
          ordered.push(id);
        }
      });
  };
  params.getAll('camera').forEach(addValue);
  params.getAll('camera_id').forEach(addValue);
  return ordered;
}

/** Append repeated `camera_id=` query params for backend UUID filtering. */
export function appendCameraIdQueryParams(params, cameraIds) {
  if (!params) return params;
  const ids = Array.isArray(cameraIds) ? cameraIds : cameraIds ? [cameraIds] : [];
  ids.forEach((id) => {
    const value = String(id || '').trim();
    if (value) params.append('camera_id', value);
  });
  return params;
}

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function extractEntryCameraId(payload = {}, overrides = {}) {
  const metadata =
    parseMaybeJson(payload.request_metadata) ||
    (payload.request_metadata && typeof payload.request_metadata === 'object'
      ? payload.request_metadata
      : {});
  const cameraDetail =
    parseMaybeJson(payload.camera_detail) ||
    parseMaybeJson(overrides.camera_detail) ||
    {};
  const raw =
    overrides.cameraId ??
    overrides.camera_id ??
    payload.camera_id ??
    payload.cameraId ??
    cameraDetail.camera_id ??
    cameraDetail.cameraId ??
    metadata.camera_id ??
    metadata.cameraId ??
    metadata.x_camera_id ??
    metadata['x-camera-id'] ??
    null;
  const id = raw != null ? String(raw).trim() : '';
  return id || null;
}

/** Client camera filter: keep the entry only when its physical camera UUID is selected. */
export function matchesWebviewCamera(entry, cameraIds) {
  if (!Array.isArray(cameraIds) || !cameraIds.length) return true;
  const id = String(extractEntryCameraId(entry, entry) || '').trim().toLowerCase();
  return Boolean(id) && cameraIds.includes(id);
}

export async function fetchCameraTypeById(apiBaseUrl) {
  const response = await fetch(`${apiBaseUrl}/api/v1/camera-streams`, {
    headers: buildWebviewFetchHeaders(),
  });
  if (!response.ok) return {};
  const data = await response.json();
  const map = {};
  (Array.isArray(data?.items) ? data.items : []).forEach((cam) => {
    const id = String(cam.id || cam.camera_id || '').trim().toLowerCase();
    const type = String(cam.camera_type || '').toUpperCase();
    if (id && (type === 'IN' || type === 'OUT')) {
      map[id] = type;
    }
  });
  return map;
}

// ─── WebSocket URL ────────────────────────────────────────────────────────────
/**
 * Build the live-stream WebSocket URL.
 * Sends Bearer token as query `token` when present (required when backend WEBVIEW_STREAM_REQUIRE_AUTH is on).
 * Intrusion webview also sets is_intrusion=true for server-side filtering.
 */

// export function buildWebviewWebSocketUrl(laneNumber = null, isIntrusion = false) {

function buildAuthenticatedWebSocketUrl(path, extraParams = {}) {
  const params = new URLSearchParams();
  // if (laneNumber != null && laneNumber !== '') {
  //   params.append('lane_number', String(laneNumber));
  // }
  // if (isIntrusion) {
  //   params.append('is_intrusion', 'true');
  // }
  Object.entries(extraParams).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      if (item != null && item !== '') {
        params.append(key, String(item));
      }
    });
  });
  const token = getStoredToken();
  if (token) {
    params.append('token', token);
  }
  const queryString = params.toString();
  const suffix = queryString ? `?${queryString}` : '';

  const hostname =
    typeof window !== 'undefined' && window.location?.hostname
      ? window.location.hostname
      : 'localhost';

  // Localhost (CRA dev) may use an explicit WS endpoint from env.
  if (LOCALHOST_HOSTNAMES.has(hostname)) {
    const envWsUrl = (process.env.REACT_APP_WS_URL || '').trim();
    if (envWsUrl) {
      try {
        const configured = new URL(envWsUrl);
        const mergedParams = new URLSearchParams(configured.search || '');
        const seenKeys = new Set();
        params.forEach((value, key) => {
          if (seenKeys.has(key)) {
            mergedParams.append(key, value);
          } else {
            mergedParams.set(key, value);
            seenKeys.add(key);
          }
        });
        const merged = mergedParams.toString();
        configured.pathname = path;
        configured.search = merged ? `?${merged}` : '';
        return configured.toString();
      } catch {
        /* fall through to default builders */
      }
    }
    // Dev default: hit backend port directly.
    return `ws://${hostname}:8000${path}${suffix}`;
  }

  // Production / LAN / Cloudflare tunnel: always same-origin page host
  // (nginx proxies /ws → :8000). Do not hardcode IP or domain.
  if (typeof window !== 'undefined' && window.location) {
    const { protocol, host } = window.location;
    const wsProto = protocol === 'https:' ? 'wss' : 'ws';
    return `${wsProto}://${host}${path}${suffix}`;
  }

  return `ws://${hostname}${path}${suffix}`;
}

/**
 * Build the live-stream WebSocket URL.
 * Sends Bearer token as query `token` when present (required when backend WEBVIEW_STREAM_REQUIRE_AUTH is on).
 * Intrusion webview also sets is_intrusion=true for server-side filtering.
 */
export function buildWebviewWebSocketUrl(laneNumber = null, isIntrusion = false, cameraIds = null) {
  const extraParams = {};
  if (laneNumber != null && laneNumber !== '') {
    extraParams.lane_number = String(laneNumber);
  }
  if (isIntrusion) {
    extraParams.is_intrusion = 'true';
  }
  const ids = Array.isArray(cameraIds) ? cameraIds : cameraIds ? [cameraIds] : [];
  const normalized = ids.map((id) => String(id || '').trim()).filter(Boolean);
  if (normalized.length) {
    extraParams.camera_id = normalized;
  }
  return buildAuthenticatedWebSocketUrl('/ws/stream', extraParams);
}

/** Unified dashboard WebSocket (attendance + entry traffic + recognition pie). */
export function buildDashboardUnifiedWebSocketUrl({
  pieDate,
  trafficDate,
  tzOffsetMinutes,
}) {
  return buildAuthenticatedWebSocketUrl('/ws/dashboard', {
    single_date: pieDate,
    date: trafficDate,
    tz_offset_minutes: tzOffsetMinutes,
  });
}

// ─── Audio ────────────────────────────────────────────────────────────────────

/** Public GET WAV path segment (appended under `/api/v1` by buildApiUrl). */
export function buildPublicEmployeeVoicePath(fetchId, voiceType) {
  return `/integrations/hrms/employees/${encodeURIComponent(String(fetchId))}/voice/${encodeURIComponent(String(voiceType))}`;
}

/** Tiny valid WAV — used only to satisfy browser autoplay / user-gesture unlock. */
export const WEBVIEW_SILENT_AUDIO_DATA_URI =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';

/**
 * Call once from a click/tap handler so later socket-driven Audio.play() is allowed.
 */
export async function unlockWebviewAudioPlayback() {
  const audio = new Audio(WEBVIEW_SILENT_AUDIO_DATA_URI);
  audio.volume = 0.001;
  try {
    await audio.play();
  } finally {
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  }
}

/**
 * First tap/click/key anywhere on the window unlocks audio (browser policy).
 * Returns a cleanup function that removes listeners.
 */
export function attachFirstGestureWebviewAudioUnlock(onUnlocked) {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const eventNames = ['pointerdown', 'click', 'touchstart'];
  let removed = false;
  let inFlight = false;
  async function onGesture() {
    if (inFlight || removed) return;
    inFlight = true;
    try {
      await unlockWebviewAudioPlayback();
      if (removed) return;
      removed = true;
      eventNames.forEach((ev) => window.removeEventListener(ev, onGesture, true));
      if (typeof onUnlocked === 'function') {
        onUnlocked();
      }
    } catch {
      /* keep listening until a gesture succeeds */
    } finally {
      inFlight = false;
    }
  }
  eventNames.forEach((ev) =>
    window.addEventListener(ev, onGesture, { capture: true, passive: true })
  );
  return () => {
    if (removed) return;
    removed = true;
    eventNames.forEach((ev) => window.removeEventListener(ev, onGesture, true));
  };
}

/**
 * Creates a strictly sequential attendance voice player.
 * @param {{
 *   isUnblocked: () => boolean,
 *   onPlaybackStart?: () => void,
 *   onPlaybackEnd?: () => void,
 * }} opts
 */
export function createAttendanceVoicePlayer({ isUnblocked, onPlaybackStart, onPlaybackEnd }) {
  let chain = Promise.resolve();
  const player = typeof Audio !== 'undefined' ? new Audio() : null;
  if (player && 'playsInline' in player) {
    try {
      player.playsInline = true;
    } catch {
      /* ignore */
    }
  }

  const playOneJob = async (job) => {
    if (!isUnblocked() || !player) return;
    let objectUrl = null;
    let didStart = false;
    try {
      player.pause();
      player.removeAttribute('src');
      player.load();

      const path = buildPublicEmployeeVoicePath(job.fetchId, job.voiceType);
      const wavUrl = buildApiUrl(path);
      const headers = { Accept: 'audio/wav' };
      const token = getStoredToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(wavUrl, { headers });
      if (!res.ok) return;
      const blob = await res.blob();
      if (!blob.size) return;

      objectUrl = URL.createObjectURL(blob);
      player.src = objectUrl;
      player.load();

      try {
        await player.play();
        didStart = true;
        onPlaybackStart?.();
      } catch {
        return;
      }

      await new Promise((resolve) => {
        const cleanup = () => {
          player.onended = null;
          player.onerror = null;
        };
        player.onended = () => { cleanup(); resolve(); };
        player.onerror = () => { cleanup(); resolve(); };
      });
    } catch {
      /* ignore */
    } finally {
      if (didStart) onPlaybackEnd?.();
      try {
        player.pause();
        player.removeAttribute('src');
        player.load();
      } catch (_) { /* ignore */ }
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl); } catch (_) { /* ignore */ }
      }
    }
  };

  return {
    enqueue(job) {
      if (!job?.fetchId || !job?.voiceType) return;
      // Sequential queue: no overlap; dedupe for *who* speaks is WebView shouldDisplayEntry + getEntryKey.
      chain = chain.then(() => playOneJob(job)).catch(() => {});
    },
  };
}
